import { HeroImage } from '@/components/display/hero-image';
import { QueryState } from '@/components/layout/query-state';
import { PageTitle } from '@/components/seo/page-title';
import { Rating } from '@/components/ui/rating-distribution';
import { useLikeCheckIn } from '@/features/activity/hooks/use-like-check-in';
import { FriendPickerSheet } from '@/features/friend-recommendations/components/friend-picker-sheet';
import { SaveToListSheet } from '@/features/lists/components/save-to-list-sheet';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useLocalSearchParams } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';

import { BusinessInfoCard } from './business-info-card';
import { CheckInsFeed } from './check-ins-feed';
import {
  useMenuItemDetailData,
  useMenuItemNoteActions,
  useMenuItemRecommendActions,
} from './hooks';
import { NoteCard } from './note-card';
import { RecommendationsCard } from './recommendations-card';

export function MenuItemDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const theme = useAppTheme();
  const { toggleLike } = useLikeCheckIn();
  const [saveSheetVisible, setSaveSheetVisible] = useState(false);
  const [recommendSheetVisible, setRecommendSheetVisible] = useState(false);

  const data = useMenuItemDetailData(id);
  const noteActions = useMenuItemNoteActions();
  const recommendActions = useMenuItemRecommendActions(id || '');

  const sortedNotes = [...data.notes].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  return (
    <QueryState
      isLoading={data.isLoadingDetail}
      error={data.detailError}
      onRetry={() => data.refetchDetail()}
      loadingMessage="Loading menu item..."
      fullScreen={false}
    >
      {data.menuItem && (
        <View className="flex-1">
          <PageTitle title={data.menuItem.name || 'Menu Item'} />

          <ScrollView showsVerticalScrollIndicator={false}>
            <HeroImage
              imageUrl={data.heroImageUrl}
              title={data.menuItem.name}
              subtitle={'at ' + (data.business?.name || data.menuItem.businessName)}
              tags={data.business?.cuisineType ? [data.business.cuisineType] : []}
              placeholderName={data.menuItem.name}
              onActionPress={() => setSaveSheetVisible(true)}
              actionIcon="bookmark-outline"
              actionLabel="Recommend"
            />

            <View className="px-4 pt-3">
              <View className="items-start mb-2">
                <View className="flex-row items-center">
                  <Rating distribution={data.aggregateDistribution} />
                </View>
              </View>

              <BusinessInfoCard menuItem={data.menuItem} business={data.business} />
              <RecommendationsCard recommendations={data.recommendations} />

              {data.isLoggedIn && (
                <View className="flex-row items-center mb-3" style={{ gap: 8 }}>
                  <ActionButton
                    icon="bookmark-outline"
                    label="Save to List"
                    onPress={() => setSaveSheetVisible(true)}
                  />
                  <ActionButton
                    icon="gift-outline"
                    label="Recommend"
                    onPress={() => setRecommendSheetVisible(true)}
                  />
                </View>
              )}

              {data.menuItem.description && (
                <Text className="text-base mb-4 leading-6 text-foreground">
                  {data.menuItem.description}
                </Text>
              )}

              <CheckInsFeed
                feedItems={data.feedItems}
                isLoading={data.isLoadingFeed}
                hasNextPage={data.hasNextPage}
                isFetchingNextPage={data.isFetchingNextPage}
                fetchNextPage={data.fetchNextPage}
                onLike={toggleLike}
              />

              {data.isLoggedIn && (
                <View className="mb-8">
                  <View className="flex-row items-center mb-4">
                    <Text className="text-lg font-semibold text-foreground" accessibilityRole="header">
                      My Notes
                    </Text>
                    {data.notes.length > 0 && (
                      <View className="ml-2 rounded-full px-2 py-0.5 bg-surface-secondary">
                        <Text className="text-xs font-medium text-muted-foreground">
                          {data.notes.length}
                        </Text>
                      </View>
                    )}
                  </View>

                  {data.isLoadingNotes && (
                    <View className="py-8 items-center">
                      <ActivityIndicator color={theme.colors.primary} />
                    </View>
                  )}

                  {!data.isLoadingNotes && data.notes.length === 0 && (
                    <View
                      className="items-center py-6 rounded-xl border border-border/50 bg-surface"
                      style={{ borderCurve: 'continuous' }}
                    >
                      <Ionicons name="document-text-outline" size={28} className="text-muted-foreground/50" />
                      <Text className="text-sm text-muted-foreground/70 mt-2 text-center px-4">
                        No notes yet. Add a tasting note after your next check-in.
                      </Text>
                    </View>
                  )}

                  {!data.isLoadingNotes && data.notes.length > 0 && (
                    <View>
                      {sortedNotes.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          onEdit={noteActions.handleEditNote}
                          onDelete={noteActions.handleDeleteNote}
                        />
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>
          </ScrollView>

          <SaveToListSheet
            itemType="menu_item"
            itemId={id || ''}
            visible={saveSheetVisible}
            onClose={() => setSaveSheetVisible(false)}
          />

          <FriendPickerSheet
            menuItemId={id || ''}
            visible={recommendSheetVisible}
            onClose={() => setRecommendSheetVisible(false)}
            onConfirm={recommendActions.handleSendRecommendations}
          />
        </View>
      )}
    </QueryState>
  );
}

function ActionButton({
  icon,
  label,
  onPress,
}: {
  icon: 'bookmark-outline' | 'gift-outline';
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center rounded-lg border border-border bg-surface px-3 py-2"
      style={{ borderCurve: 'continuous' }}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Ionicons name={icon} size={16} className="text-foreground" />
      <Text className="ml-1.5 text-sm font-semibold text-foreground">{label}</Text>
    </Pressable>
  );
}
